import { api, fetchData, postData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { Moment, MomentErrorResponse } from '../types/api';
import { ResourceState } from '../types/evalform';
import { change_state } from './formUtils';

export interface FormState {
  name: string;
  mmt_category: string;
  mmt_category_id: number;
  is_active: string;
  is_active_id: number;
  state_id: ResourceState;
  new_state_id?: ResourceState;
  language_id: number;
  sentiment_id: number;
  occurrence_id: number;
  speaker_id: number;
  search_method_id: number;
  prompt: string;
  examples: string;
  channels: number[];
}

export interface FormErrors {
  [key: string]: string[];
}

// Check if a moment name already exists
export const checkNameExists = async (name: string, currentId?: number): Promise<boolean> => {
  if (!name.trim()) return false; // Don't check empty names
  
  try {
    const response = await fetchData<{moments: Moment[]}>(
      `${api(PATH_SEGMENTS.MOMENT)}?name=${encodeURIComponent(name)}`
    );

    if (response.moments && response.moments.length > 0) {
      // Don't show error for the current moment in edit mode
      if (currentId && response.moments[0].id === currentId) {
        return false;
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error checking name existence:", error);
    return false;
  }
};

// Validate form data
export const validateFormData = async (formState: FormState, currentId?: number): Promise<FormErrors> => {
  const errors: FormErrors = {};

  // Required field validation
  if (!formState.name) {
    errors.name = ['Name is required'];
  }

  // Check name uniqueness (only for new moments or when name changed)
  // This is commented out for efficiency avoiding mulitple calls to the API because this check is done when form is submitted
//   if (formState.name && !currentId) {
//     const nameExists = await checkNameExists(formState.name, currentId);
//     if (nameExists) {
//       errors.name = ['A moment with this name already exists'];
//     }
//   }

  // Required field validation
  if (!formState.prompt) {
    errors.prompt = ['Prompt is required'];
  }

  if(!formState.mmt_category_id) {
    errors.mmt_category_id = ['Moment category is required'];
  }

  return errors;
};

// Prepare payload for API
export const prepareMomentPayload = (formState: FormState, action: 'save' | 'submit' = 'save', currentId?: number) => {
  const payload: any = {
    ...formState,
    // Set state_id to NEW if not already defined
    state_id: formState.state_id || ResourceState.NEW,
    // Set new_state_id based on action
    new_state_id: change_state(action, formState.state_id || ResourceState.NEW),
    // Convert channels array to required format
    channels: formState.channels.map(channelId => ({ channel_id: channelId })),
    // Include moment category ID
    mmt_category_id: formState.mmt_category_id,
  };
  
  // Add ID for updates
  if (currentId) {
    payload.id = currentId;
  }
  
  return payload;
};

// Submit moment data
export const submitMomentData = async (
  formState: FormState, 
  action: 'save' | 'submit' = 'save',
  currentId?: number
): Promise<{ success: boolean; errors?: FormErrors; mmt_id?: number }> => {
  try {
    // Validate form data
    const errors = await validateFormData(formState, currentId);
    if (Object.keys(errors).length > 0) {
      return { success: false, errors };
    }

    // Prepare the payload
    const payload = prepareMomentPayload(formState, action, currentId);

    // Debug logging
    console.log('submitMomentData - currentId:', currentId, 'payload:', payload);

    // Build query parameters for new moments
    const queryParams = !currentId ? '?action=' + action : '';

    // Send request with proper endpoint and payload structure
    const endpoint = currentId 
      ? api(PATH_SEGMENTS.MOMENT, currentId.toString()) 
      : `${api(PATH_SEGMENTS.MOMENT)}${queryParams}`;
    
    console.log('submitMomentData - endpoint:', endpoint, 'method:', currentId ? 'PUT' : 'POST');
    
    const response = await postData<MomentErrorResponse>(endpoint, payload, currentId ? 'PUT' : 'POST');
    
    if (response.errors) {
      return { success: false, errors: response.errors };
    }

    return { success: true, mmt_id: response.mmt_id };
  } catch (error) {
    console.error('Submit error:', error);
    return { 
      success: false, 
      errors: { general: ['An unexpected error occurred. Please try again.'] } 
    };
  }
};

// Handle input change with validation
export const handleFormInputChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  formState: FormState,
  setFormState: (state: FormState) => void,
  errors: FormErrors,
  setErrors: (errors: FormErrors) => void
) => {
  const { name, value } = e.target;
  
  // For dropdowns/selects, convert to number
  if (['language_id', 'sentiment_id', 'occurrence_id', 'speaker_id', 'search_method_id', 'is_active_id', 'mmt_category_id'].includes(name)) {
    setFormState({
      ...formState,
      [name]: Number(value)
    });
  } else {
    setFormState({
      ...formState,
      [name]: value
    });
  }
  
  // Clear errors for this field if any
  if (errors[name]) {
    const newErrors = { ...errors };
    delete newErrors[name];
    setErrors(newErrors);
  }
};

// Handle channel change
export const handleChannelChange = (
  channelId: number, 
  checked: boolean, 
  formState: FormState,
  setFormState: (state: FormState) => void,
  errors: FormErrors,
  setErrors: (errors: FormErrors) => void
) => {
  const newChannels = [...formState.channels];
  
  if (checked && !newChannels.includes(channelId)) {
    newChannels.push(channelId);
  } else if (!checked && newChannels.includes(channelId)) {
    const index = newChannels.indexOf(channelId);
    newChannels.splice(index, 1);
  }
  
  setFormState({
    ...formState,
    channels: newChannels
  });
  
  // Clear errors for this field if any
  if (errors.channels) {
    const newErrors = { ...errors };
    delete newErrors.channels;
    setErrors(newErrors);
  }
};

// Convert Moment object to FormState
export const momentToFormState = (moment: Moment): FormState => {
  return {
    name: moment.name,
    mmt_category: moment.mmt_category,
    mmt_category_id: moment.mmt_category_id || 1, // Default to first category if not set
    is_active: moment.is_active,
    is_active_id: moment.is_active === 'True' ? 1 : 0,
    state_id: moment.state_id,
    language_id: moment.language_id,
    sentiment_id: moment.sentiment_id,
    occurrence_id: moment.occurrence_id,
    speaker_id: moment.speaker_id,
    search_method_id: moment.search_method_id,
    prompt: moment.prompt,
    examples: moment.examples || '',
    channels: moment.channels?.map(ch => Number(ch.channel_id)) || [1]
  };
};
